import React, { useState } from 'react';
import { Building2, Calendar, DollarSign, FileText, Home, MessageSquare, Receipt, MapPin, ArrowRight, PenLine, CheckCircle2, Clock, AlertTriangle, Smartphone, User, CreditCard, Download, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useTenant } from './TenantContext';
import { tenantAPI } from '../../lib/api';
import { toast } from 'sonner';

const TenantOverview = () => {
  const { profile, receipts, requests, agencySettings, formatCurrency, setIsRequestDialogOpen, isInstallable, isIOS, isInstalled, installApp, setShowIOSInstructions, setActiveTab } = useTenant();
  const [downloadingPm, setDownloadingPm] = useState(false);
  const downloadPaymentMethods = async () => {
    setDownloadingPm(true);
    try {
      const res = await tenantAPI.downloadPaymentMethodsPdf();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `modes_paiement_${(profile?.tenant?.name || 'locataire').replace(/\s+/g, '_').toLowerCase()}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche téléchargée');
    } catch (e) {
      toast.error('Impossible de télécharger la fiche');
    } finally {
      setDownloadingPm(false);
    }
  };
  return (
    <>
  {/* Property Info Card */}
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-lg flex items-center gap-2">
        <Building2 className="w-5 h-5 text-[#D4A373]" />
        Mon Logement
      </CardTitle>
    </CardHeader>
    <CardContent>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="font-semibold text-lg text-gray-900">
            {profile?.property?.title}
          </p>
          <p className="text-gray-500">
            {profile?.property?.address}
          </p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-sm text-gray-500">Loyer mensuel</p>
          <p className="text-2xl font-bold text-[#1B3A4B]">
            {formatCurrency(profile?.property?.monthly_rent, profile?.property?.currency)}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>

  {/* Stats */}
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total payé</p>
            <p className="text-xl font-bold text-gray-900">
              {formatCurrency(profile?.stats?.total_paid || 0, profile?.property?.currency)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
    
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Quittances</p>
            <p className="text-xl font-bold text-gray-900">
              {profile?.stats?.payments_count || 0}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className={profile?.stats?.unpaid_amount > 0 ? 'border-red-200 bg-red-50' : ''}>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            profile?.stats?.unpaid_amount > 0 ? 'bg-red-100' : 'bg-green-100'
          }`}>
            <Receipt className={`w-6 h-6 ${
              profile?.stats?.unpaid_amount > 0 ? 'text-red-600' : 'text-green-600'
            }`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500">Solde impayé</p>
            <p className={`text-xl font-bold ${
              profile?.stats?.unpaid_amount > 0 ? 'text-red-600' : 'text-green-600'
            }`}>
              {profile?.stats?.unpaid_amount > 0
                ? formatCurrency(profile.stats.unpaid_amount, profile?.property?.currency)
                : 'À jour'
              }
            </p>
            {profile?.stats?.months_unpaid > 0 && (
              <p className="text-xs text-red-500">
                {profile.stats.months_unpaid} mois de retard
              </p>
            )}
            {profile?.stats?.pending_count > 0 && (
              <div
                className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5"
                data-testid="tenant-pending-badge"
                title="Paiements enregistrés par l'agence, en attente de règlement"
              >
                <Clock className="w-3 h-3" />
                {profile.stats.pending_count} paiement{profile.stats.pending_count > 1 ? 's' : ''} en attente
                {' · '}
                {formatCurrency(profile.stats.pending_amount, profile?.property?.currency)}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  </div>

  {/* Liste des paiements enregistres et en attente de reglement */}
  {(profile?.pending_payments?.length || 0) > 0 && (
    <Card className="border-amber-200" data-testid="tenant-pending-payments-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
          <Clock className="w-5 h-5 text-amber-600" />
          Paiements enregistrés en attente
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Ces paiements ont été enregistrés par l&apos;agence mais restent à régler de votre côté.
        </p>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-amber-100">
          {profile.pending_payments.map((p) => {
            const period = (p.period_year && p.period_month)
              ? new Date(p.period_year, p.period_month - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
              : null;
            return (
              <div key={p.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`tenant-pending-payment-${p.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {p.receipt_number || 'Paiement'}{period ? ` — ${period}` : ''}
                  </p>
                  <p className="text-[11px] text-gray-500">
                    {p.payment_method ? `Mode : ${p.payment_method}` : ''}
                    {p.reference ? ` · Réf : ${p.reference}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-amber-800 tabular-nums">
                    {formatCurrency(p.amount, p.currency || profile?.property?.currency || 'XOF')}
                  </p>
                  <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px]">
                    En attente
                  </Badge>
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  )}

  {/* Quick Actions */}
  <Card>
    <CardHeader>
      <CardTitle className="text-lg">Actions rapides</CardTitle>
    </CardHeader>
    <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <Button 
        variant="outline" 
        className="justify-start"
        onClick={downloadPaymentMethods}
        disabled={downloadingPm}
        data-testid="quick-action-payment-methods"
      >
        {downloadingPm ? (
          <Loader2 className="w-4 h-4 mr-2 animate-spin text-[#D4A373]" />
        ) : (
          <CreditCard className="w-4 h-4 mr-2 text-[#D4A373]" />
        )}
        Modes de paiement
      </Button>
      <Button 
        variant="outline" 
        className="justify-start"
        onClick={() => setActiveTab('receipts')}
        data-testid="quick-action-receipts"
      >
        <Receipt className="w-4 h-4 mr-2 text-[#D4A373]" />
        Voir mes quittances
      </Button>
      <Button 
        variant="outline" 
        className="justify-start"
        onClick={() => setActiveTab('notices')}
        data-testid="quick-action-notices"
      >
        <AlertTriangle className="w-4 h-4 mr-2 text-orange-500" />
        Avis d'échéance
      </Button>
      <Button 
        variant="outline" 
        className="justify-start"
        onClick={() => {
          setActiveTab('requests');
          setIsRequestDialogOpen(true);
        }}
        data-testid="quick-action-request"
      >
        <MessageSquare className="w-4 h-4 mr-2 text-blue-500" />
        Faire une demande
      </Button>
    </CardContent>
  </Card>

  {/* Install App Card */}
  {(isInstallable || (isIOS && !isInstalled)) && (
    <Card className="bg-gradient-to-r from-[#1B3A4B] to-[#2a6b55] text-white">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-medium">Installer l'application</p>
              <p className="text-sm text-white/80">Accédez rapidement depuis votre écran d'accueil</p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="bg-white text-[#1B3A4B] hover:bg-white/90"
            onClick={() => {
              if (isIOS) {
                setShowIOSInstructions(true);
              } else {
                installApp();
              }
            }}
            data-testid="install-app-btn"
          >
            Installer
          </Button>
        </div>
      </CardContent>
    </Card>
  )}

  {/* Contact Info */}
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm text-gray-500">Besoin d'aide ?</p>
            <p className="text-sm font-medium">Contactez votre agence {agencySettings?.name || "GAB'S SERVICES HABITAT"}</p>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
    </>
  );
};

export default TenantOverview;
